import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { CentroLogisticoCementoVm } from '../../../../models/centro-logistico-cemento.vm';
import { CentroLogisticoCementoUpsertCommand } from '../../../../models/centro-logistico-cemento.model';

function nonEmptyTrimmedValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (typeof raw !== 'string' || raw.trim() === '') {
    return { required: true };
  }
  return null;
}

type CentroLogisticoForm = FormGroup<{
  puestoExploracion: FormControl<string>;
  centro: FormControl<string>;
  centroNombre: FormControl<string>;
  planta: FormControl<string>;
}>;

type ControlKey = keyof CentroLogisticoForm['controls'];

@Component({
  selector: 'app-centro-logistico-cemento-detalle-modal',
  templateUrl: './centro-logistico-cemento-detalle-modal.component.html',
  styleUrls: ['./centro-logistico-cemento-detalle-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CentroLogisticoCementoDetalleModalComponent implements OnChanges {
  @Input() item: CentroLogisticoCementoVm | null = null;
  @Input() loading = false;

  @Output() closeRequested = new EventEmitter<void>();
  @Output() saveRequested = new EventEmitter<CentroLogisticoCementoUpsertCommand>();
  @Output() deleteRequested = new EventEmitter<void>();

  readonly form: CentroLogisticoForm;

  constructor(private readonly fb: FormBuilder) {
    this.form = this.fb.group({
      puestoExploracion: this.fb.nonNullable.control('', { validators: [nonEmptyTrimmedValidator] }),
      centro: this.fb.nonNullable.control('', { validators: [nonEmptyTrimmedValidator] }),
      centroNombre: this.fb.nonNullable.control('', { validators: [nonEmptyTrimmedValidator] }),
      planta: this.fb.nonNullable.control('', { validators: [nonEmptyTrimmedValidator] }),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item']) {
      this.applyItemToForm();
    }
  }

  get isEditMode(): boolean {
    return (this.item?.id ?? 0) > 0;
  }

  get modalTitle(): string {
    return this.isEditMode ? 'Editar centro logístico' : 'Crear centro logístico';
  }

  fieldError(controlName: ControlKey): string {
    const control = this.form.controls[controlName];
    if (!control.touched || !control.invalid) {
      return '';
    }
    const errors = control.errors;
    if (errors?.['required']) {
      return 'Campo requerido';
    }
    return 'Revise el valor';
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saveRequested.emit({
      puestoExploracion: this.form.controls.puestoExploracion.getRawValue().trim(),
      centro: this.form.controls.centro.getRawValue().trim(),
      centroNombre: this.form.controls.centroNombre.getRawValue().trim(),
      planta: this.form.controls.planta.getRawValue().trim(),
    });
  }

  onClose(): void {
    this.closeRequested.emit();
  }

  onDelete(): void {
    this.deleteRequested.emit();
  }

  private applyItemToForm(): void {
    if (!this.item) {
      this.form.reset({
        puestoExploracion: '',
        centro: '',
        centroNombre: '',
        planta: '',
      });
      return;
    }

    this.form.reset({
      puestoExploracion: this.item.puestoExploracion,
      centro: this.item.centro,
      centroNombre: this.item.centroNombre,
      planta: this.item.planta,
    });
  }
}
